import axios from 'axios';

/**
 * Single-round-trip polling helper for the FS App Manager dashboard.
 *
 * Hits the optimised `/api/builds/dashboard-tick` endpoint, which combines
 * the activity-feed delta with the per-app status payload that the deploy
 * table needs. Two existing polling channels (`/summary` every 8 s,
 * `/app-status` every 12 s) collapse into one cycle that ships only the
 * builds whose `updatedAt > serverTime` since the last tick.
 *
 * Stateful piece: the caller tracks `feedSince` between ticks. The util
 * exposes the bookkeeping shape via the typed return value so callers don't
 * have to thread timestamps through their own state.
 */

export interface DashboardTickFeedItem {
  buildId: number;
  appId: number;
  composerHost: string;
  currentPhase: string | null;
  version: string;
  os: string | null;
  submitter: string | null;
  createdAt: string;
  updatedAt: string;
  pipelineUrl: string | null;
  pipelineStatus: string;
}

export interface DashboardTickAppStatus {
  currentPhase: string | null;
  pipelineStatus: string | null;
  last5: Array<{ status: string; createdAt: string }>;
}

export interface DashboardTickResponse {
  serverTime: string;
  feedDelta: DashboardTickFeedItem[];
  feedHasMore: boolean;
  appStatus: Record<number, DashboardTickAppStatus>;
}

export interface DashboardTickParams {
  /** App ids whose status the table is currently rendering. */
  appIds?: number[];
  /** ISO timestamp from the previous tick. Omit for the first call. */
  feedSince?: string;
  /** Max delta rows. Default matches the server default of 50. */
  feedLimit?: number;
  /** Offset for the no-since first-load case. */
  feedOffset?: number;
}

const URL_PATH = '/api/builds/dashboard-tick';

/**
 * Fire a single dashboard tick.
 *
 * Caller is responsible for storing the returned `serverTime` and passing
 * it back as `feedSince` on the next tick. On the first call (no `feedSince`)
 * the server returns up to `feedLimit` most-recent rows so the client can
 * seed its in-memory store before switching to incremental polling.
 */
export async function fetchDashboardTick(params: DashboardTickParams = {}): Promise<DashboardTickResponse> {
  const search = new URLSearchParams();
  if (params.appIds && params.appIds.length > 0) {
    search.set('appIds', params.appIds.join(','));
  }
  if (params.feedSince) search.set('feedSince', params.feedSince);
  if (params.feedLimit) search.set('feedLimit', String(params.feedLimit));
  if (params.feedOffset != null) search.set('feedOffset', String(params.feedOffset));

  const tok = (() => { try { return localStorage.getItem('token'); } catch { return null; } })();
  const qs = search.toString();
  const url = qs ? `${URL_PATH}?${qs}` : URL_PATH;
  const resp = await axios.get<DashboardTickResponse>(url, {
    headers: tok ? { Authorization: `Bearer ${tok}` } : {},
  });
  return resp.data;
}
